import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';

import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { mutate } from 'swr';

import { POST, PUT } from '@/api/base';
import { ENDPOINTS } from '@/api/endpoints';
import { ROUTES } from '@/router/routes';
import { US_STATES_ARRAY } from '@/libs/us-states';
import { isUSCountry } from '@/utils/country-states';
import { toast_success } from '@/libs/toast-wrappers';
import { useGetSiteById } from '@/hooks/api/use-get-site-by-id';
import { useCountriesOptions } from '@/hooks/api/use-countries-options';
import { useGetCamerasFullList } from '@/hooks/api/use-get-cameras-full-list';

import { Hr } from '@/components/ui/divider';
import { InputField } from '@/components/form/input-field';
import { Switch } from '@/components/form/switch';
import { SelectField } from '@/components/form/select-field';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/form/textarea';

const createSchema = (t: TFunction) =>
  z.object({
    description: z.string().trim().min(1, t('site-description-error')),
    timezone: z.string().trim(),
    active: z.boolean(),
    city: z.string().trim().min(1, t('city-error')),
    country: z.string().trim().min(1, t('country-error')),
    postal_code: z.string().trim().min(1, t('postal-code-error')),
    province_or_state: z.string().trim().min(1, t('province-or-state-error')),
    street_address: z.string().trim().min(1, t('street-address-error')),
    default_camera: z.string().trim().optional(),
  });

type InputTypes = z.infer<ReturnType<typeof createSchema>>;

const defaultValues = {
  description: '',
  timezone: '',
  active: false,
};

export function SiteForm() {
  const [saving, setSaving] = useState(false);
  const { siteId, clinicId } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(siteId);

  const { COUNTRIES } = useCountriesOptions();
  const { site, loading: loadingSite } = useGetSiteById({ site_id: siteId });
  const { cams, loading: loadingCams } = useGetCamerasFullList();

  const loading = loadingSite || loadingCams;

  const { t } = useTranslation();

  const schema = useMemo(() => createSchema(t), [t]);

  const {
    register,
    handleSubmit,
    setValue,
    control,
    watch,
    formState: { errors },
  } = useForm<InputTypes>({ defaultValues, resolver: zodResolver(schema) });

  const { onFormClose } = useOutletContext<{
    onFormClose: () => Promise<void>;
  }>();

  const country = watch('country');
  const isUSASelected = useMemo(() => isUSCountry(country), [country]);

  const onSubmitUpdateSite: SubmitHandler<InputTypes> = async (data) => {
    setSaving(true);
    if (isEditing && siteId) {
      const response = await PUT(
        `${ENDPOINTS.SITE_BY_ID}`,
        { clinic_id: site?.clinic_id, ...data },
        { parameters: { siteId } }
      );

      if (response) {
        await onFormClose();
        await mutate([ENDPOINTS.SITES, clinicId]);
        toast_success(t('site-updated').replace('clinicId', siteId.slice(0, 5)));
        setSaving(false);
        return;
      }
    }

    const response = await POST(ENDPOINTS.SITES, { clinic_id: clinicId, ...data });

    if (response) {
      await onFormClose();
      await mutate([ENDPOINTS.SITES, clinicId]);
      toast_success(t('site-saved'));
      setSaving(false);
    }

    setSaving(false);
  };

  const fillForm = useCallback(() => {
    setValue('description', site?.description ?? '');
    setValue('timezone', site?.timezone ?? '');
    setValue('active', site?.active ?? false);
    setValue('country', site?.country ?? '');
    setValue('street_address', site?.street_address ?? '');
    setValue('province_or_state', site?.province_or_state ?? '');
    setValue('city', site?.city ?? '');
    setValue('postal_code', site?.postal_code ?? '');
    setValue('default_camera', site?.default_camera ?? '');
  }, [site, setValue]);

  useEffect(() => {
    fillForm();
  }, [fillForm, site]);

  return (
    <form onSubmit={handleSubmit(onSubmitUpdateSite)}>
      <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2 mb-8">
        <div className="sm:col-span-3">
          <InputField
            disabled={loading || saving}
            autoFocus
            autoComplete="organization"
            required
            label={t('site-name')}
            errorMessage={errors.description?.message}
            {...register('description')}
          />
        </div>
        <div className="sm:col-span-3">
          <InputField
            disabled={loading || saving}
            autoComplete="off"
            label={t('timezone-label')}
            {...register('timezone')}
          />
        </div>
        <div className="sm:col-span-3">
          <SelectField
            disabled={loading || saving}
            autoComplete="country"
            aria-label={t('country-label')}
            required
            label={t('country-label')}
            errorMessage={errors.country?.message}
            options={COUNTRIES ?? []}
            enableEmptyOption
            {...register('country')}
          />
        </div>

        <div className="col-span-full">
          <Textarea
            disabled={loading || saving}
            autoComplete="street-address"
            required
            label={t('address-label')}
            errorMessage={errors.street_address?.message}
            {...register('street_address')}
          />
        </div>

        <div className="sm:col-span-2 sm:col-start-1">
          <InputField
            disabled={loading || saving}
            autoComplete="address-level2"
            required
            label={t('city-label')}
            errorMessage={errors.city?.message}
            {...register('city')}
          />
        </div>

        <div className="sm:col-span-2">
          {isUSASelected ? (
            <SelectField
              disabled={loading || saving}
              autoComplete="address-level1"
              aria-label={t('province-or-state-label')}
              required
              label={t('province-or-state-label')}
              errorMessage={errors.province_or_state?.message}
              options={US_STATES_ARRAY ?? []}
              {...register('province_or_state')}
            />
          ) : (
            <InputField
              disabled={loading || saving}
              autoComplete="address-level1"
              required
              label={t('province-or-state-label')}
              errorMessage={errors.province_or_state?.message}
              {...register('province_or_state')}
            />
          )}
        </div>
        <div className="sm:col-span-2">
          <InputField
            disabled={loading || saving}
            autoComplete="postal-code"
            inputMode="numeric"
            required
            label={t('postal-code-label')}
            type="number"
            errorMessage={errors.postal_code?.message}
            {...register('postal_code')}
          />
        </div>
        <div className="sm:col-span-3">
          <SelectField
            disabled={loading || saving}
            enableEmptyOption
            autoComplete="off"
            aria-label={t('default-camera-label')}
            label={t('default-camera-label')}
            errorMessage={errors.default_camera?.message}
            options={cams ?? []}
            {...register('default_camera')}
          />
        </div>
        <fieldset className={`InputFieldBlock col-start-1`}>
          <div className="mt-5 sm:mt-0 sm:flex sm:flex-shrink-0 sm:items-center">
            <label className="input-label mr-4">{t('is-active-label')}</label>
            <Controller
              name="active"
              control={control}
              render={({ field }) => (
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  disabled={loading || saving}
                />
              )}
            />
          </div>
        </fieldset>
      </div>
      <Hr />
      <div className="mt-6 flex items-center justify-end">
        {isEditing && isUSASelected && (
          <div className="flex-1">
            <Button
              variant="link"
              onClick={() =>
                navigate(String(ROUTES.ADMIN_EDIT_SITE_NPI_PREFERRED).split('/').at(-1) ?? '', {
                  state: { site },
                })
              }
            >
              {t('site-edit-preferred-npi')}
            </Button>
          </div>
        )}
        <div className="mt-6 flex items-center justify-end gap-x-2">
          <Button onClick={onFormClose} variant="ghost-muted" disabled={saving}>
            {t('cancel')}
          </Button>
          <Button type="submit" loading={saving}>
            {t('save')}
          </Button>
        </div>
      </div>
    </form>
  );
}
